import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { S, Icon, StatusBadge, BtnPrimary, EmptyState, formatCHF, formatDate } from '../regureStyles';
import PermissionGate from '../../components/PermissionGate';

// Offer-scoped label overrides. `sent` reads "Issued" because no outbound
// action happens on create — the state just means finalised/no longer
// editable. See FOLLOWUPS FU-OFF-1 for the real send-to-client work.
const OFFER_STATUS_LABEL_KEYS = {
  draft:    'offers.list.statusDraft',
  sent:     'offers.list.statusSent',
  accepted: 'offers.list.statusAccepted',
  declined: 'offers.list.statusDeclined',
  expired:  'offers.list.statusExpired',
};

// Select-all checkbox for the current PAGE.
//
// `indeterminate` is a DOM property with no HTML attribute behind it, so it
// has to be set through a ref — writing it as a JSX prop renders a plain
// unchecked box and the "some of this page is selected" state never shows.
const TriStateCheckbox = ({ checked, indeterminate, onChange, label }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate && !checked);
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type='checkbox'
      checked={checked}
      onChange={onChange}
      aria-label={label}
      title={label}
      style={{ width: 15, height: 15, cursor: 'pointer', accentColor: S.burgundy, margin: 0 }}
    />
  );
};

const Table = ({
  paginated, setSelectedOffer, setShowNew,
  selectedIds, toggleRow, togglePage,
}) => {
  const { t } = useTranslation();

  // Rows on THIS page that can be selected. An offer with no _id cannot be
  // named in an ids export, so it gets no checkbox rather than a checkbox
  // that silently does nothing.
  const pageIds = paginated.map(o => o._id).filter(Boolean);
  const selectedOnPage = pageIds.filter(id => selectedIds.has(id)).length;
  const allOnPage = pageIds.length > 0 && selectedOnPage === pageIds.length;

  const columnHeaders = [
    t('offers.list.colOfferNumber'),
    t('offers.list.colClient'),
    t('offers.list.colProduct'),
    t('offers.list.colPremium'),
    t('offers.list.colStatus'),
    t('offers.list.colCreated'),
    t('offers.list.colExpires'),
    t('offers.list.colBroker'),
    // GESTIONNAIRE. AROMED, tracker R34 (04.09.2026): "Rajouter une colonne
    // avec le nom du gestionnaire qui traite la demande (voir si la
    // proposition a été établie par le courtier ou par AMS)". Deliberately
    // NOT the broker column: every AMS-prepared quote also names the
    // brokerage the business belongs to, so `broker` can never answer who
    // prepared it.
    t('offers.list.colHandler', { defaultValue: 'Handler' }),
  ];

  const thStyle = {
    padding: '13px 14px',
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 700,
    color: S.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    borderBottom: `2px solid ${S.borderLight}`,
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '0 32px' }}>
      {paginated.length === 0 ? (
        <EmptyState
          icon='calculator'
          title={t('offers.list.emptyTitle')}
          description={t('offers.list.emptyDescription')}
          action={
            <PermissionGate resource='offers' action='create'>
              <BtnPrimary onClick={() => setShowNew(true)}>
                <Icon name='plus' color='#fff' size={16} /> {t('offers.list.newQuote')}
              </BtnPrimary>
            </PermissionGate>
          }
        />
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: 36, paddingRight: 0 }}>
                <TriStateCheckbox
                  checked={allOnPage}
                  indeterminate={selectedOnPage > 0}
                  onChange={() => togglePage(pageIds, !allOnPage)}
                  label={t('offers.list.selectPage')}
                />
              </th>
              {columnHeaders.map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.map((o, i) => {
              const isSelected = Boolean(o._id) && selectedIds.has(o._id);
              // A selected row is tinted so the selection is legible at a
              // glance rather than only in the checkbox column — the whole
              // point of the count in the toolbar is that nobody exports a
              // set they cannot see.
              const baseBg = isSelected
                ? 'rgba(128,0,32,0.05)'
                : (i % 2 === 0 ? '#fff' : S.bgCard);
              return (
                <tr
                  key={o.id}
                  style={{ borderBottom: `1px solid ${S.borderLight}`, background: baseBg, transition: 'background 0.15s' }}
                  onMouseEnter={e => (e.currentTarget.style.background = S.bgHover)}
                  onMouseLeave={e => (e.currentTarget.style.background = baseBg)}
                >
                  <td style={{ padding: '13px 0 13px 14px' }}>
                    {o._id && (
                      <input
                        type='checkbox'
                        checked={isSelected}
                        onChange={() => toggleRow(o._id)}
                        aria-label={t('offers.list.selectRow', { number: o.id })}
                        style={{ width: 15, height: 15, cursor: 'pointer', accentColor: S.burgundy, margin: 0 }}
                      />
                    )}
                  </td>
                  <td style={{ padding: '13px 14px' }}>
                    <button
                      onClick={() => setSelectedOffer(o)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        fontSize: 13,
                        fontWeight: 700,
                        color: S.burgundy,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        textDecoration: 'underline',
                        textDecorationColor: S.burgundy + '50',
                      }}
                    >
                      {o.id}
                    </button>
                  </td>
                  <td style={{ padding: '13px 14px', fontSize: 13, fontWeight: 600, color: S.textPrimary }}>
                    {o.client || '—'}
                    {/* A proposal that has been revised carries its version
                        here rather than as a second row — the list shows one
                        row per proposal (tracker R33). */}
                    {Number(o.variant) > 1 && (
                      <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: S.textMuted }}>
                        v{o.variant}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '13px 14px', fontSize: 13, color: S.textSecondary }}>{o.product || '—'}</td>
                  <td style={{ padding: '13px 14px', fontSize: 13, fontWeight: 600, color: S.textPrimary }}>{formatCHF(o.premiumCalc)}</td>
                  <td style={{ padding: '13px 14px' }}>
                    <StatusBadge status={o.status} label={OFFER_STATUS_LABEL_KEYS[o.status] ? t(OFFER_STATUS_LABEL_KEYS[o.status]) : undefined} />
                  </td>
                  <td style={{ padding: '13px 14px', fontSize: 12, color: S.textMuted }}>{formatDate(o.created)}</td>
                  <td style={{ padding: '13px 14px', fontSize: 12, color: o.status === 'expired' ? S.danger : S.textMuted }}>{formatDate(o.expires)}</td>
                  <td style={{ padding: '13px 14px', fontSize: 13, color: S.textSecondary }}>{o.broker || '—'}</td>
                  <td style={{ padding: '13px 14px', fontSize: 13, color: S.textSecondary, whiteSpace: 'nowrap' }}>
                    {o.handlerName || '—'}
                    {o.preparedBy && (
                      <span style={{
                        marginLeft: 6, fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
                        textTransform: 'uppercase', padding: '2px 5px', borderRadius: 3,
                        color: o.preparedBy === 'broker' ? '#1e40af' : S.burgundy,
                        background: o.preparedBy === 'broker' ? '#dbeafe' : 'rgba(128,0,32,0.08)',
                      }}>
                        {o.preparedBy === 'broker'
                          ? t('offers.list.preparedByBroker', { defaultValue: 'Broker' })
                          : t('offers.list.preparedByAms', { defaultValue: 'AMS' })}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Table;
